import React from "react";
import cns from "classnames";
import { Divider, Grid, makeStyles, Menu, SvgIcon, Typography } from "@material-ui/core";
import { ThemeType } from "@theme";
import { mdiFileExcel } from "../common/svgs";
import locale from "@bonliva-crm/locale";
import Body from "@components/typography/Body";
import RegularButton from "@components/buttons/RegularButton";
import { SortOption, SortOptions } from "@search/SortOptions";
import SendVoucherFortnoxMenu from "@bookings/BookingSearch/send-voucher-fortnox-menu/send-voucher-fortnox-menu";
import { BookingCSVReportType } from "@graphql";
import { store } from "@store";
import MenuItem from "@material-ui/core/MenuItem";
import { OkCancelDialogOptions } from "@containers";
import { WithTooltip } from "@common";
import moment from "moment";
import { isContinuityRangeTooLong } from "@bonliva-crm/shared";

type Props<T extends LabelValue[]> = {
  activeGroupBy?: T[number]["value"];
  groupBy?: T;
  onGroupBy?: (v: T[number]) => void;
  onExcel?: (type: BookingCSVReportType) => void;
  onExcelBg?: (type: BookingCSVReportType) => void;
  isExcelLoading?: boolean;
  sortOptions?: SortOption[];
  onSortOption?: (v: SortOption) => void;
  dateRange?: { from: Date; to: Date };
};

export function SearchOptions<T extends LabelValue[] = any>(props: Props<T>) {
  const classes = useStyles({});
  const { activeGroupBy, groupBy, onGroupBy, sortOptions, onSortOption } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const showRightPane = props.onExcel;

  const continuityTooLong = isContinuityRangeTooLong(
    props.dateRange?.from,
    props.dateRange?.to
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (type: BookingCSVReportType, inBackground = false) => {
    if (type === BookingCSVReportType.Continuity && continuityTooLong) {
      handleClose();
      store()
        .ui.confirmDialog.confirm(
          new OkCancelDialogOptions(locale.BookingReportMaxMonthsDatePeriod)
        );
      return;
    }

    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions(getConfirmationText(type)))
      .then((res) => {
        if (res === "ok") {
          handleClose();
          if (inBackground) {
            props.onExcelBg(type);
          } else {
            props.onExcel(type);
          }
        } else {
          handleClose();
        }
      });
  };

  return (
    <Grid container spacing={0} className={classes.root}>
      {groupBy && (
        <Grid item xs={6} className={classes.groupBy}>
          <Body className={classes.groupByTitle}>{locale.GroupBy}:</Body>

          {groupBy.map((gr) => (
            <Body
              className={cns(classes.item, {
                [classes.link]: activeGroupBy !== gr.value
              })}
              key={gr.value}
              onClick={() => onGroupBy && onGroupBy(gr)}
            >
              {gr.label.toLocaleLowerCase()}
            </Body>
          ))}
        </Grid>
      )}

      {sortOptions && (
        <Grid container alignItems="flex-end" item xs={6} className={classes.sortOption}>
          <SortOptions sortOptions={sortOptions} onSortOption={onSortOption} />
        </Grid>
      )}
      {showRightPane && (
        <Grid item xs={6} className={classes.rightPane}>
          <RegularButton
            disabled={props.isExcelLoading}
            size="small"
            variant="text"
            style={{ marginLeft: "auto" }}
            leftIcon={
              <SvgIcon fontSize="small">
                <path d={mdiFileExcel} />
              </SvgIcon>
            }
            loading={props.isExcelLoading}
            loadingContent={locale.Downloading}
            onClick={handleClick}
          >
            {locale.BookingReportsMenu}
          </RegularButton>
          <Menu
            id="booking-reports-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            MenuListProps={{ disablePadding: true }}
          >
            <Typography style={{ padding: "8px 16px", fontWeight: 500 }}>
              {locale.BookingReportsMenu}
            </Typography>
            {props.dateRange && (
              <Typography
                variant="caption"
                style={{ padding: "0 16px 8px", color: "gray", display: "block" }}
              >
                {moment(props.dateRange.from).format("YYYY-MM-DD")} –{" "}
                {moment(props.dateRange.to).format("YYYY-MM-DD")}
              </Typography>
            )}
            {continuityTooLong && (
              <Typography
                variant="caption"
                style={{
                  padding: "0 16px 8px",
                  color: "#f57c00",
                  display: "block"
                }}
              >
                {locale.bookingCSVReportType[BookingCSVReportType.Continuity]}:{" "}
                {locale.BookingReportMaxMonthsDatePeriod}
              </Typography>
            )}
            <Divider />

            {/* Group 1: Bokningsrapporter */}
            <Typography
              variant="caption"
              style={{ padding: "8px 16px 4px", color: "gray", display: "block" }}
            >
              Bokningsrapporter
            </Typography>
            <WithTooltip
              title={
                "Rapporten speglar den filtrering som är gjord på bokningssidan och inkluderar all information."
              }
            >
              <MenuItem
                disabled={!store().user.can("booking-report:export-bookings:all")}
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.Bookings as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.Bookings}
              </MenuItem>
            </WithTooltip>
            <WithTooltip
              title={
                continuityTooLong
                  ? locale.BookingReportMaxMonthsDatePeriod
                  : "Rapporten visar antalet timmar en konsult har arbetat per vecka. Konsultnamn är ersatt med konsultlänk."
              }
            >
              <span>
                <MenuItem
                  disabled={
                    !store().user.can("booking-report:export-continuity:all") ||
                    continuityTooLong
                  }
                  onClick={() =>
                    handleMenuItemClick(
                      BookingCSVReportType.Continuity as BookingCSVReportType,
                      true
                    )
                  }
                >
                  {locale.bookingCSVReportType.Continuity}
                </MenuItem>
              </span>
            </WithTooltip>

            <Divider style={{ margin: "8px 0" }} />

            {/* Group 2: Ekonomiska poster */}
            <Typography
              variant="caption"
              style={{ padding: "8px 16px 4px", color: "gray", display: "block" }}
            >
              Ekonomiska poster
            </Typography>
            <Typography
              variant="caption"
              style={{
                padding: "0 16px 4px",
                color: "#f57c00",
                display: "block"
              }}
            >
              Dessa rapporter använder endast datumfilter
            </Typography>
            <WithTooltip
              title={
                'Rapporten inkluderar alla veckor i valt tidsspann som antingen är i status "Utbetald" eller "Fakturerad", eller har en kundfaktura som ännu inte är bokförd. Notera att rapporten reagerar enbart på start- och slutdatum i filtren.'
              }
            >
              <MenuItem
                disabled={!store().user.can("booking-report:export-half-open-records:all")}
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.HalfOpenRecords as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.HalfOpenRecords}
              </MenuItem>
            </WithTooltip>
            <WithTooltip
              title={
                'Rapporten inkluderar alla veckor i valt tidsspann som antingen är i status "Öppen", "Utbetald" eller "Fakturerad", eller har en kundfaktura som ännu inte är bokförd. Notera att rapporten reagerar enbart på start- och slutdatum i filtren.'
              }
            >
              <MenuItem
                disabled={
                  !store().user.can("booking-report:export-open-half-open-records:all")
                }
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.OpenHalfOpenRecords as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.OpenHalfOpenRecords}
              </MenuItem>
            </WithTooltip>
            <WithTooltip
              title={
                'Rapporten inkluderar alla veckor i valt tidsspann som har status "Öppen". Notera att rapporten reagerar enbart på start- och slutdatum i filtren.'
              }
            >
              <MenuItem
                disabled={!store().user.can("booking-report:export-open-posts:all")}
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.OpenPosts as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.OpenPosts}
              </MenuItem>
            </WithTooltip>
            <WithTooltip title={"Projektavstämningsrapport"}>
              <MenuItem
                disabled={
                  !store().user.can("booking-report:export-project-reconciliation:all")
                }
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.ProjectReconciliation as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.ProjectReconciliation}
              </MenuItem>
            </WithTooltip>
            <WithTooltip
              title={
                'Rapporten inkluderar alla veckor i valt tidsspann som har status "Stängd" (fakturerade och utbetalda). Inkluderar vilken prislista som använts. Notera att rapporten reagerar enbart på start- och slutdatum i filtren.'
              }
            >
              <MenuItem
                disabled={!store().user.can("booking-report:export-bookings:all")}
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.ClosedBookingWeeks as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.ClosedBookingWeeks}
              </MenuItem>
            </WithTooltip>

            <Divider style={{ margin: "8px 0" }} />

            {/* Group 3: Statistik & Analys */}
            <Typography
              variant="caption"
              style={{ padding: "8px 16px 4px", color: "gray", display: "block" }}
            >
              Statistik & Analys
            </Typography>
            <WithTooltip title={locale.bookingNationalProcurementStatisticsInfo}>
              <MenuItem
                disabled={!store().user.can("booking-report:national-statistics:all")}
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.NationalProcurementStatistics as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.NationalProcurementStatistics}
              </MenuItem>
            </WithTooltip>
            <WithTooltip title={"SAHLR-statusrapport för bokningar"}>
              <MenuItem
                disabled={!store().user.can("booking-report:export-sahlr-status:all")}
                onClick={() => handleMenuItemClick(BookingCSVReportType.SAHLRStatus, true)}
              >
                {locale.bookingCSVReportType.SAHLRStatus}
              </MenuItem>
            </WithTooltip>
            <WithTooltip
              title={
                "Se TB över tid för enskilda konsulter. Du kommer enbart kunna se de veckor där du är del av projektet. Ledning, OC, BK kan se alla."
              }
            >
              <MenuItem
                disabled={
                  !store().user.can(
                    "booking-report:export-profitability-per-consultant:all"
                  )
                }
                onClick={() =>
                  handleMenuItemClick(
                    BookingCSVReportType.ProfitabilityPerConsultant as BookingCSVReportType,
                    true
                  )
                }
              >
                {locale.bookingCSVReportType.ProfitabilityPerConsultant}
              </MenuItem>
            </WithTooltip>
          </Menu>
          <SendVoucherFortnoxMenu />
        </Grid>
      )}
    </Grid>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    padding: 8
  },

  groupBy: {
    // padding: 8,
    display: "flex",
    alignItems: "center"
  },

  sortOption: {
    paddingRight: theme.spacing(4),
    textAlign: "right"
  },

  groupByTitle: {
    display: "inline-block",
    marginRight: theme.spacing(1 / 2)
  },

  item: {
    display: "inline-block",
    marginRight: theme.spacing(1 / 2),

    "&:not(:last-child):after": {
      content: "'|'",
      display: "inline-block",
      marginLeft: theme.spacing(1 / 2)
    }
  },

  link: {
    cursor: "pointer",
    fontWeight: theme.fontWeight.xBold
  },

  rightPane: {
    display: "flex",
    alignItems: "center"
  }
}));

const getConfirmationText = (type: BookingCSVReportType) => {
  switch (type) {
    case BookingCSVReportType.Continuity:
      return "Du håller på att ladda ner rapport Kontinuitet, vill du fortsätta?";
    case BookingCSVReportType.Bookings:
      return "Du håller på att ladda ner rapport Bokningar, vill du fortsätta?";
    case BookingCSVReportType.HalfOpenRecords:
      return "Du håller på att ladda ner rapporten för Halvöppna poster - tänk på att rapporten enbart tar hänsyn till filtret för datum, inget annat filter.";
    case BookingCSVReportType.OpenHalfOpenRecords:
      return "Du håller på att ladda ner rapporten för Öppna + Halvöppna poster - tänk på att rapporten enbart tar hänsyn till filtret för datum, inget annat filter.";
    case BookingCSVReportType.OpenPosts:
      return "Du håller på att ladda ner rapporten för Öppna poster - tänk på att rapporten enbart tar hänsyn till filtret för datum, inget annat filter.";
    default:
      return `Du håller på att ladda ner rapport ${locale.bookingCSVReportType[type]}?`;
  }
};

export default SearchOptions;
